import {Injectable} from "@angular/core";
import {HttpContext} from "@angular/common/http";
import {HttpClientWrapperService} from "../../../../../common/service/http-client-wrapper.service";
import {map, Observable} from "rxjs";
import {AddressModel} from "../../../../../../../tal-library/lib-google-map/common/google-map.model";
import {
    Address,
    OrganizationMinOutDto
} from "src/app/talenlio-hireos/home-layout/common/service/organization/organization.service";
import {ContactPersonOutDto} from "../../../../../common/model/contact-person.model";
import {MEMBER_MODULE, MEMBER_TAB} from "../../../../../common/model/user.model";

@Injectable({
    providedIn: 'root'
})
export class MemberService {

    private employersUrl = '/organizations';
    private employerMembersUrl = '/members';
    private statusUrl = '/status';
    private resendInviteUrl = '/resendInvite';
    private supportUsersUrl = '/supportUsers';
    private aiJobAgentUrl = '/aiJobAgent';
    private tabAccessUrl = '/tabAccess';

    constructor(private httpClientWrapperService: HttpClientWrapperService) {
    }

    findAll(organizationId: string, httpContext: HttpContext = new HttpContext()): Observable<MemberOutDto[]> {
        return this.httpClientWrapperService.getWithHttpContext<MemberOutDto[]>(`${this.getUrl(organizationId)}`, [], httpContext);
    }

    findAllInternal(organizationId: string): Observable<MemberOutDto[]> {
        return this.findAll(organizationId).pipe(
            map(memberOutDtos => memberOutDtos?.filter(memberOutDto => memberOutDto?.memberType !== MEMBER_TYPE.EXTERNAL))
        );
    }

    findAllExternal(organizationId: string): Observable<MemberOutDto[]> {
        return this.findAll(organizationId).pipe(
            map(memberOutDtos => memberOutDtos?.filter(memberOutDto => memberOutDto?.memberType === MEMBER_TYPE.EXTERNAL))
        );
    }

    find(organizationId: string, employerHasMemberId: string): Observable<MemberOutDto> {
        return this.httpClientWrapperService.get<MemberOutDto>(`${this.getUrl(organizationId)}/${employerHasMemberId}`);
    }

    create(organizationId: string, employerHasMemberInDto: MemberInDto): Observable<MemberOutDto> {
        return this.httpClientWrapperService.post<MemberOutDto>(`${this.getUrl(organizationId)}`, employerHasMemberInDto);
    }

    update(organizationId: string, employerHasMemberId: string, memberInDto: MemberInDto): Observable<MemberOutDto> {
        return this.httpClientWrapperService.put<MemberOutDto>(`${this.getUrl(organizationId)}/${employerHasMemberId}`, memberInDto);
    }

    delete(organizationId: string, employerHasMemberId: string) {
        return this.httpClientWrapperService.delete(`${this.getUrl(organizationId)}/${employerHasMemberId}`);
    }

    updateStatus(organizationId: string, employerHasMemberId: string, memberStatusInDto: MemberStatusInDto): Observable<MemberOutDto> {
        return this.httpClientWrapperService.patch<MemberOutDto>(`${this.getUrl(organizationId)}/${employerHasMemberId}${this.statusUrl}`, memberStatusInDto);
    }

    updateAiJobAgentAccess(organizationId: string, memberId: string,
                           memberAiJobAgentInDto: MemberAiJobAgentInDto): Observable<MemberOutDto> {
        return this.httpClientWrapperService.patch<MemberOutDto>(
            `${this.getSupportUrl(organizationId)}/${memberId}${this.aiJobAgentUrl}`, memberAiJobAgentInDto);
    }

    updateTabAccess(organizationId: string, memberId: string,
                    memberTabAccessInDto: MemberTabAccessInDto): Observable<MemberModuleAccessOutDto[]> {
        return this.httpClientWrapperService.put<MemberModuleAccessOutDto[]>(
            `${this.getSupportUrl(organizationId)}/${memberId}${this.tabAccessUrl}`, memberTabAccessInDto);
    }

    resendInvitation(organizationId: string, employerHasMemberId: string) {
        return this.httpClientWrapperService.patch(`${this.getUrl(organizationId)}/${employerHasMemberId}${this.resendInviteUrl}`);
    }

    private getUrl(organizationId: string): string {
        return `${this.employersUrl}/${organizationId}${this.employerMembersUrl}`;
    }

    private getSupportUrl(organizationId: string): string {
        return `${this.supportUsersUrl}${this.employersUrl}/${organizationId}${this.employerMembersUrl}`;
    }
}

export class MemberOutDto {
    id: string;
    name: string;
    address: Address;
    emailAddress: string;
    contactNumber: string;
    memberType: MEMBER_TYPE;
    memberStatus: MEMBER_STATUS;
    organizationMinOutDto: OrganizationMinOutDto;
    profileImageUrl: string;
    hasAccessToAiJobAgent?: boolean;
    enabledMemberTabs?: MEMBER_TAB[];
    createdAt: string;
    lastModifiedAt: string;
}

export class MemberAiJobAgentInDto {
    hasAccessToAiJobAgent: boolean;
}

export class MemberTabAccessInDto {
    enabledMemberTabs: MEMBER_TAB[];
}

export class MemberModuleAccessOutDto {
    memberModule: MEMBER_MODULE;
    enabled: boolean;
    memberTabAccessOutDtos: MemberTabAccessOutDto[];
}

export class MemberTabAccessOutDto {
    memberTab: MEMBER_TAB;
    enabled: boolean;
}

export class MemberInDto {
    name?: string;
    address?: AddressModel;
    emailAddress?: string;
    contactNumber?: string;
    memberType?: MEMBER_TYPE;
    memberStatus?: MEMBER_STATUS;
    profileImageUrl?: string;
}

export class MemberMinOutDto {
    id: string;
    memberType: MEMBER_TYPE;
    profileImageUrl: string;
    name: string;
    emailAddress: string;
    memberStatus: MEMBER_STATUS;
}

export class MemberStatusInDto {
    memberStatus: MEMBER_STATUS;
}

export enum MEMBER_STATUS {
    PENDING_REQUEST = 'PENDING_REQUEST',
    ACTIVATED = 'ACTIVATED',
    DEACTIVATED = 'DEACTIVATED'
}

export enum MEMBER_STATUS_DISPLAY {
    PENDING_REQUEST = 'Pending',
    ACTIVATED = 'Activated',
    DEACTIVATED = 'Deactivated'
}

export enum MEMBER_TYPE {
    OWNER = 'OWNER',
    ADMIN = 'ADMIN',
    MEMBER = 'MEMBER',
    EXTERNAL = 'EXTERNAL',
}

export enum MEMBER_TYPE_DISPLAY {
    OWNER = 'Super Admin', //Renamed to Super Admin
    ADMIN = 'Admin',
    MEMBER = 'Member',
    EXTERNAL = 'External'
}
